// src/AuthContext.js

import React, { createContext, useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import API_BASE from "./config";

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [userData, setUserData] = useState(null);
    const [loading, setLoading] = useState(true);
    const refreshTimer = useRef(null);
    const doRefreshRef = useRef(null); // breaks the scheduleRefresh <-> doRefresh cycle

    // -----------------------------
    // Logout (used by App.js and refresh failures)
    // -----------------------------
    const logout = useCallback(() => {
        if (refreshTimer.current) clearTimeout(refreshTimer.current);
        setUserData(null);

        localStorage.removeItem("idToken");
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");

        const domain = process.env.REACT_APP_COGNITO_DOMAIN;
        const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID;
        const responseType = process.env.REACT_APP_COGNITO_RESPONSE_TYPE;
        const scope = process.env.REACT_APP_COGNITO_SCOPE;
        const logoutRedirect = encodeURIComponent(
            process.env.REACT_APP_COGNITO_LOGOUT_REDIRECT_URI
        );

        window.location.href = `https://${domain}/login?client_id=${clientId}&response_type=${responseType}&scope=${scope}&redirect_uri=${logoutRedirect}`;
    }, []);

    // -----------------------------
    // Schedule a silent refresh ~2 min before the idToken expires.
    // Active users get a fresh token; the session only ends when the
    // Cognito refresh token itself expires (then /auth/refresh fails).
    // -----------------------------
    const scheduleRefresh = useCallback((exp) => {
        if (refreshTimer.current) clearTimeout(refreshTimer.current);
        if (!exp) return;
        const delay = Math.max(0, exp * 1000 - Date.now() - 120000);
        refreshTimer.current = setTimeout(() => {
            if (doRefreshRef.current) doRefreshRef.current();
        }, delay);
    }, []);

    // -----------------------------
    // Exchange the refresh-token cookie for a fresh idToken.
    // Returns true on success (and reschedules the next refresh).
    // -----------------------------
    const doRefresh = useCallback(async () => {
        try {
            const res = await axios.post(
                `${API_BASE}/auth/refresh`,
                {},
                { withCredentials: true }
            );
            if (res.data && res.data.authenticated) {
                setUserData(res.data);
                scheduleRefresh(res.data.exp);
                return true;
            }
        } catch (_) {
            /* refresh token expired/revoked — session is truly over */
        }
        return false;
    }, [scheduleRefresh]);

    useEffect(() => {
        doRefreshRef.current = doRefresh;
    }, [doRefresh]);

    // -----------------------------
    // Initial session load. If the idToken is gone/expired, try one
    // silent refresh before treating the user as logged out.
    // -----------------------------
    useEffect(() => {
        const init = async () => {
            try {
                const res = await axios.get(`${API_BASE}/auth/session`, {
                    withCredentials: true
                });
                if (res.data.authenticated) {
                    setUserData(res.data);
                    scheduleRefresh(res.data.exp);
                } else if (!(await doRefresh())) {
                    setUserData(null);
                }
            } catch (err) {
                if (!(await doRefresh())) setUserData(null);
            } finally {
                setLoading(false);
            }
        };

        init();
        return () => {
            if (refreshTimer.current) clearTimeout(refreshTimer.current);
        };
    }, [scheduleRefresh, doRefresh]);

    const isAuthenticated = !!userData?.authenticated;

    return (
        <AuthContext.Provider
            value={{
                userData,
                loading,
                isAuthenticated,
                logout,
                refresh: doRefresh
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};
